import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount, DEFAULT_PERIOD_STATE } from '../../components/common/AdvancedFilter/periodUtils';
import { GenericView, SearchInput, ActionButtons, ColumnToggle } from '../../components/common/table';
import ExpenseForm from './components/ExpenseForm';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getExpenses, deleteExpense, updateExpense, createExpense } from './api/expenseApi';
import api from '../../api';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';

const Expense = () => {
  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters('expense');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({
    ...DEFAULT_PERIOD_STATE,
    dateFrom: '',
    dateTo: '',
    category: '',
    company: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    ...DEFAULT_PERIOD_STATE,
    dateFrom: '',
    dateTo: '',
    category: '',
    company: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  useEffect(() => {
    const saved = loadPersistedState();
    if (saved) {
      const restoredFilters = saved.filters ?? { ...DEFAULT_PERIOD_STATE, dateFrom: '', dateTo: '', category: '', company: '' };
      const restoredSearch = saved.search ?? '';
      const restoredPage = saved.page ?? 0;
      const restoredSize = saved.size ?? 10;

      setFilterData(restoredFilters);
      setAppliedFilters(restoredFilters);
      setSearchQuery(restoredSearch);
      setPage(restoredPage);
      setSize(restoredSize);

      loadExpenses(restoredPage, restoredSize, restoredFilters);
    } else {
      loadExpenses(0, size);
    }
  }, []); // Only load on mount, filters are applied via handleFilterApply

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount, currencyCode = 'IDR') => {
    if (!amount) return '-';
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '-';
    
    const locale = currencyCode === 'USD' ? 'en-US' : 
                   currencyCode === 'EUR' ? 'de-DE' :
                   currencyCode === 'GBP' ? 'en-GB' :
                   currencyCode === 'JPY' ? 'ja-JP' :
                   currencyCode === 'SGD' ? 'en-SG' :
                   currencyCode === 'AUD' ? 'en-AU' :
                   'id-ID';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(numAmount);
  };

  const loadExpenses = async (pageNum = page, pageSize = size, filters = appliedFilters) => {
    setIsLoading(true);
    try {
      const params = {
        page: pageNum,
        size: pageSize,
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
      };
      const response = await getExpenses(params);
      const list = Array.isArray(response) ? response : (response?.content ?? []);
      setExpenses(list);
      if (response && typeof response.totalElements === 'number') {
        setTotalElements(response.totalElements);
        setTotalPages(response.totalPages ?? Math.max(1, Math.ceil(response.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading expenses:', error);
      showAlert('danger', 'Failed to load expenses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const empty = {
      ...DEFAULT_PERIOD_STATE,
      dateFrom: '',
      dateTo: '',
      category: '',
      company: '',
    };
    setFilterData(empty);
    setAppliedFilters(empty);
    setSearchQuery('');
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
    loadExpenses(0, size, empty);
  };

  const handleFilterApply = () => {
    const periodRange = getActivePeriodRange(filterData);
    const resolved = {
      ...filterData,
      dateFrom: periodRange.startDate || filterData.dateFrom,
      dateTo: periodRange.endDate || filterData.dateTo,
    };
    setAppliedFilters(resolved);
    setPage(0);
    persistState({ filters: resolved, search: searchQuery, page: 0, size });
    loadExpenses(0, size, resolved);
    window.bootstrap?.Offcanvas?.getInstance(document.getElementById('offcanvasExpense'))?.hide();
  };

  // Filter and search data
  const filteredData = expenses.filter(item => {
    // ✅ SEARCH: Filter by search query
    const matchesSearch = !searchQuery || 
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.categoryName && item.categoryName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.companyName && item.companyName.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    // ✅ DATE FILTER: Apply date filters using appliedFilters (only when Apply is clicked)
    // Note: Backend filtering is primary, this is just a fallback for client-side filtering
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      if (!item.expenseDate) return false;
      
      const expenseDate = new Date(item.expenseDate);
      expenseDate.setHours(0, 0, 0, 0);
      
      // Check dateFrom
      if (appliedFilters.dateFrom) {
        const fromDate = new Date(appliedFilters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (expenseDate < fromDate) return false;
      }
      
      // Check dateTo
      if (appliedFilters.dateTo) {
        const toDate = new Date(appliedFilters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (expenseDate > toDate) return false;
      }
    }
    
    return true;
  });

  const filterFields = [];

  const activeCount = getActiveFilterCount(appliedFilters, filterFields);

  const filterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${appliedFilters.period.replace(/_/g, ' ')}`
      });
    } else if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      const label = [appliedFilters.dateFrom, appliedFilters.dateTo].filter(Boolean).join(' → ');
      tags.push({ key: 'dateRange', label: `Date: ${label}` });
    }

    return tags;
  }, [appliedFilters]);

  const handleRemoveFilter = (key) => {
    if (key === 'period' || key === 'dateRange') {
      const updated = {
        ...appliedFilters,
        period: '',
        dateFrom: '',
        dateTo: '',
        startDate: '',
        endDate: '',
      };
      setAppliedFilters(updated);
      setFilterData(prev => ({
        ...prev,
        period: '',
        dateFrom: '',
        dateTo: '',
        startDate: '',
        endDate: '',
      }));
      setPage(0);
      loadExpenses(0, size, updated);
    }
  };

  const defaultVisibleColumns = ['expenseDate', 'companyName', 'projectName', 'categoryName', 'description', 'fileName', 'amount'];
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);

  const columns = [
    { 
      key: 'expenseDate', 
      label: 'Date',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '120px', width: '120px' }}
          title={formatDateToDDMMYYYY(value)}
        >
          {formatDateToDDMMYYYY(value)}
        </span>
      )
    },
    { 
      key: 'companyName', 
      label: 'Company Name',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'tagging', 
      label: 'Tagging',
      className: 'text-truncate',
      render: (value) => {
        if (!value) return <span className="text-muted">-</span>;
        const tags = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    { 
      key: 'projectName', 
      label: 'Project Name',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'categoryName', 
      label: 'Category',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'description', 
      label: 'Description',
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '250px', width: '250px' }}
          title={value}
        >
          {value || '-'}
        </span>
      )
    },
    { 
      key: 'fileName', 
      label: 'File',
      className: 'text-truncate',
      render: (value, row) => {
        if (!value) return '-';
        return (
          <button
            type="button"
            className="btn btn-link text-primary text-decoration-none p-0 border-0"
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                const response = await api.get(`/crm/expenses/download/${row.id}`, {
                  responseType: 'blob'
                });
                
                // Extract original filename (remove timestamp prefix if exists)
                let originalFileName = value;
                if (originalFileName && originalFileName.includes("_")) {
                  const lastUnderscoreIndex = originalFileName.lastIndexOf("_");
                  if (lastUnderscoreIndex > 0) {
                    originalFileName = originalFileName.substring(lastUnderscoreIndex + 1);
                  }
                }
                
                // Create blob URL and download the file
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = originalFileName;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error('Error downloading file:', error);
                showAlert('danger', 'Failed to download file. Please try again.');
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M14 3v4a1 1 0 0 0 1 1h4" />
              <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
              <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
              <path d="M17 18h2" />
              <path d="M20 15h-3v6" />
              <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
            </svg>
            View
          </button>
        );
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      className: 'text-truncate',
      render: (value, row) => {
        const num = parseFloat(value);
        if (!value || isNaN(num)) return <span>-</span>;
        const code = (row.currencyCode || 'IDR').toUpperCase();
        const rate = parseFloat(row.exchangeRateAtCreation) || 1;
        const idrAmount = code === 'IDR' ? num : num * rate;
        const display = 'Rp ' + idrAmount.toLocaleString('id-ID', { maximumFractionDigits: 0 });
        const tooltip = code !== 'IDR'
          ? `${code} ${num.toLocaleString('id-ID')} × ${rate.toLocaleString('id-ID')} = ${display}`
          : display;
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={tooltip}
          >
            {display}
          </span>
        );
      }
    }
  ];

  const handleFormSubmit = async (formData) => {
    console.log('handleFormSubmit called with:', formData);
    console.log('selectedExpense:', selectedExpense);
    
    try {
      if (selectedExpense) {
        console.log('Updating expense with ID:', selectedExpense.id);
        await updateExpense(selectedExpense.id, formData);
        console.log('Expense updated successfully');
        showAlert('success', 'Expense updated successfully');
      } else {
        console.log('Creating new expense');
        await createExpense(formData);
        console.log('Expense created successfully');
        showAlert('success', 'Expense created successfully');
      }
      await loadExpenses(page, size);
      setIsModalOpen(false);
      setSelectedExpense(null);
    } catch (error) {
      console.error('Error saving expense:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Failed to save expense';
      showAlert('danger', errorMessage);
      // Re-throw error so form component can handle it
      throw error;
    }
  };

  const handleView = (row) => {
    navigate(`/expense/${row.id}`);
  };

  const handleEdit = (row) => {
    setSelectedExpense(row);
    setIsModalOpen(true);
  };

  const handleDelete = (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    setSelectedExpense(null);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteExpense(itemToDelete.id);
      await loadExpenses(page, size);
      showAlert('success', 'Expense deleted successfully');
    } catch (error) {
      console.error('Error deleting expense:', error);
      showAlert('danger', 'Failed to delete expense');
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };


  const handleExportExcel = () => {
    showAlert('info', 'Export to Excel functionality coming soon');
  };

  const handleExportPDF = () => {
    showAlert('info', 'Export to PDF functionality coming soon');
  };

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns(prev => (
      prev.includes(columnKey)
        ? (prev.length > 1 ? prev.filter(key => key !== columnKey) : prev)
        : [...prev, columnKey]
    ));
  };

  const handleToggleAllColumns = (show) => {
    setVisibleColumns(show ? columns.map(col => col.key) : [columns[0].key]);
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Expense Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Add Expense
            </button>
          </div>
        </div>
      </div>

      <ExpenseForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedExpense(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedExpense}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={filteredData}
                      columns={columns}
                      onEdit={handleEdit}
                      onView={handleView}
                      onDelete={handleDelete}

                      isLoading={isLoading}
                      topControlsContent={
                        <>
                          <div className="row align-items-center w-full mb-3">
                            <div className="col">
                              <div className="col-6 col-sm-4 col-md-2 col-xl">
                                <FilterButton offcanvasId="offcanvasExpense" activeCount={activeCount} />
                              </div>
                            </div>
                            <div className="col-md-auto col-sm-12">
                              <div className="ms-auto d-flex flex-wrap btn-list gap-2">
                                <SearchInput
                                  value={searchQuery}
                                  onChange={setSearchQuery}
                                  placeholder="Search expenses..."
                                />
                                <ColumnToggle
                                  columns={columns}
                                  visibleColumns={visibleColumns}
                                  onToggleColumn={handleToggleColumn}
                                  onToggleAll={handleToggleAllColumns}
                                />
                                <ActionButtons
                                  onExportExcel={handleExportExcel}
                                  onExportPDF={handleExportPDF}
                                />
                              </div>
                            </div>
                          </div>
                          <FilterAwarenessBar
                            title="Expenses is showing a filtered view"
                            summary={`Filtered by ${activeCount} ${activeCount === 1 ? 'criterion' : 'criteria'}`}
                            detail={filterTags.map(tag => tag.label).join(' · ')}
                            loading={isLoading}
                            activeCount={activeCount}
                            filterTags={filterTags}
                            onRemoveFilter={handleRemoveFilter}
                            onResetFilters={handleFilterReset}
                          />
                        </>
                      }
                      visibleColumns={visibleColumns}
                      onVisibleColumnsChange={setVisibleColumns}
                      emptyStateMessage="No expenses found"
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          loadExpenses(nextPage, size);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          loadExpenses(0, newSize);
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdvancedFilterPanel
        offcanvasId="offcanvasExpense"
        title="Filter Expenses"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
        showPeriod={true}
      />
    </div>
  );
};

export default Expense;

